import {
  OnInit,
  inject,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogTabsComponent,
  SuiDialogLeftHeaderSlot,
  SuiDialogLeftContentSlot,
  SuiDialogRightHeaderSlot,
  SuiDialogRightContentSlot,
  SuiDialogRightFooterSlot,
} from '@sui/dialog';

import { UserAccessLevel } from 'src/app/core/services/user.service';
import { UtilsService } from 'src/app/core/services/utils.service';

import { WorkspaceSettingsComponent } from '../../components/workspace-settings/workspace-settings.component';
import { AuditTrailComponent } from '../../../shared/components/audit-trail/audit-trail.component';
import { ObjectPermissionsComponent } from '../../../objects/components/object-permissions/object-permissions.component';

import { Workspace } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';

import {
  injectCreateWorkspaceMutation,
  injectUpdateWorkspaceMutation,
} from 'src/app/api/queries/workspace.queries';

import { AppState } from 'src/app/core/state/app.state';
import { selectUser } from 'src/app/core/state';

import { tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { DialogTabNavComponent } from '../../../shared/components/dialog-tab-nav/dialog-tab-nav.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

interface WorkspaceDialogInput extends TabDialogDataBase {
  workspace?: Workspace;
}

/** Close data: the submitted form plus `saved`, the in-flight request (SavedCloseData). */
interface WorkspaceDialogOutput {
  saved?: Promise<unknown>;
  [field: string]: unknown;
}

@Component({
  selector: 'app-workspace-editor-dialog',
  templateUrl: './workspace-editor-dialog.component.html',
  styleUrl: './workspace-editor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogTabsComponent,
    SuiDialogLeftHeaderSlot,
    FaIconComponent,
    SuiDialogLeftContentSlot,
    SearchInputComponent,
    DialogTabNavComponent,
    SuiDialogRightHeaderSlot,
    SuiButtonComponent,
    SuiDialogRightContentSlot,
    SuiDialogRightFooterSlot,
    TranslatePipe,
  ],
})
export class WorkspaceEditorDialogComponent
  extends TabDialogBase<WorkspaceDialogInput, WorkspaceDialogOutput>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private utilsService = inject(UtilsService);

  private createWorkspaceMutation = injectCreateWorkspaceMutation();
  private updateWorkspaceMutation = injectUpdateWorkspaceMutation();

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  // * UI state
  objectActionTitle = '';
  objectNameTitle = '';
  objectIcon: IconProp = ['fas', 'folder'];
  objectActionIcon: IconProp = ['fas', 'cog'];

  get isUpdateMode(): boolean {
    return !!this.dialogData.workspace;
  }

  get workspace(): Workspace | undefined {
    return this.dialogData.workspace;
  }

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  private setupUIState(): void {
    if (this.isUpdateMode && this.workspace) {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.edit-workspace-settings',
      );
      this.objectNameTitle = this.workspace.title;
      this.objectIcon = getIconForDataObject(
        this.workspace.object_class_id,
        this.workspace.object_type_id,
      );
    } else {
      this.objectActionTitle = this.translateService.instant(
        'popup.action-title.create-workspace',
      );
      this.objectNameTitle = this.translateService.instant(
        'popup.object-title.new-workspace',
      );
      this.objectIcon = ['fas', 'folder'];
    }
  }

  private setupTabs(): void {
    // General tab (always present)
    this.tabs = [
      {
        id: 'general',
        label: this.translateService.instant(
          'workspace-editor-popup.tab.general',
        ),
        component: WorkspaceSettingsComponent,
        inputs: {
          workspace: this.workspace,
          searchString$: this.searchString$,
        },
      },
    ];

    // * additional Tabs only in Update Mode
    if (this.isUpdateMode && this.workspace) {
      this.tabs.push(
        {
          id: 'permissions',
          label: this.translateService.instant(
            'workspace-editor-popup.tab.permissions',
          ),
          component: ObjectPermissionsComponent,
          inputs: {
            searchString$: this.searchString$,
            objects: [this.workspace],
            companyId: this.workspace.company_id,
            allowOwnerChange: true,
          },
          objectPermissions: ['can_manage'],
        },
        {
          id: 'audit-trail',
          label: this.translateService.instant(
            'workspace-editor.tab.audit-trail',
          ),
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          component: AuditTrailComponent,
          inputs: {
            searchString$: this.searchString$,
            object: this.workspace,
            hasOwner: true,
            timeCreatedKey: 'time_created',
            timeModifiedKey: 'time_modified',
          },
          objectPermissions: ['can_manage'],
        },
      );

      this.store
        .select(selectUser)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((user) =>
            this.filterTabsByObjectPermissions(this.workspace, user),
          ),
        )
        .subscribe();
    }
  }

  protected override performSave(
    formData: unknown,
  ): DialogCloseOptions<WorkspaceDialogOutput> {
    // * the dialog closes optimistically; the hook's onSuccess patches the cached
    // * list on the real response, so nothing here depends on the dialog living
    // * dotted i18n control names (title_i18n.language_id_N) must become a nested
    // * title_i18n object before hitting the wire - PHP mangles dots in param names
    const wireData = this.utilsService.convertFormDataI18nKeys(
      formData as Record<string, any>,
    );
    if (this.isUpdateMode && this.workspace) {
      const saved = this.updateWorkspaceMutation.mutateAsync({
        id: this.workspace.id,
        workspace: wireData,
      });
      saved.catch(() => undefined);
      return {
        reason: DialogCloseReason.Update,
        data: { ...(formData as object), saved },
      };
    } else {
      const data = wireData as {
        title: string;
        description: string;
        title_i18n?: Record<string, string>;
      };
      const saved = this.createWorkspaceMutation.mutateAsync({
        title: data.title,
        description: data.description,
        title_i18n: data.title_i18n,
      });
      saved.catch(() => undefined);
      return {
        reason: DialogCloseReason.Save,
        data: { ...(formData as object), saved },
      };
    }
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    // * all valid, proceed with save
    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);

    // TODO: Optional - subscribe to success$ action and close dialog - discuss what approach we want?
    // TODO: if we close on success we need to disabled buttons or give feedback to the user
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
